import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/shared";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" data-testid="login-container">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-slate-100 p-12">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-teal-500 flex items-center justify-center">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading font-bold text-lg">MarksMan Healthcare</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Project Hub</div>
          </div>
        </div>
        <div className="max-w-md">
          <h1 className="font-heading text-4xl font-bold leading-tight tracking-tight">
            Every project, from first conversation to final payment.
          </h1>
          <p className="text-slate-400 mt-4 text-sm leading-relaxed">
            Track client discussions, proposals, execution, invoicing and collections in one place, built for the
            MarksMan project team and leadership.
          </p>
        </div>
        <div className="text-xs text-slate-500">Internal use only · MarksMan Healthcare</div>
      </div>
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-slate-900">Sign in</h2>
            <p className="text-sm text-slate-500 mt-1">Use your MarksMan account to continue.</p>
          </div>
          <Field label="Email" required>
            <Input data-testid="login-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@marksmanhealthcare.com" required autoFocus />
          </Field>
          <Field label="Password" required>
            <Input data-testid="login-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </Field>
          {error && <div data-testid="login-error" className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded px-3 py-2">{error}</div>}
          <Button data-testid="login-submit-button" type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={busy}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
